import { useState } from 'react';
import { AlertTriangle, CalendarClock, Loader2 } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { getCampaignTodayString } from '@/lib/calendarUtils';
import { useAuth } from '@/contexts/AuthContext';
import {
  useRecurringCutoffSummary,
  usePullInRecurringCutoff,
  useStopAllRecurring,
  RecurringCutoffError,
} from '@/hooks/queries/useRecurringCutoff';

/**
 * ADR-069 (#616) — the recurring wind-down surface. Shows the committee's recurring availability,
 * effective cutoff, and active-series count, and lets an admin wind the committee's recurring down after a
 * loss/withdrawal: pull the cutoff in to an earlier date or stop all recurring now. PLATFORM-ADMIN ONLY: a
 * destructive campaign-wide sweep kept to platform staff — a campaign's own owner/finance manager is
 * denied here (the tab is hidden and the API is the durable 403). One campaign at a time.
 */
export function RecurringDonationsAdmin() {
  const { user } = useAuth();
  const isPlatformAdmin = user?.isPlatformAdmin ?? false;
  const { data: summary, isLoading, isError } = useRecurringCutoffSummary();
  const pullIn = usePullInRecurringCutoff();
  const stopAll = useStopAllRecurring();

  const [cutoff, setCutoff] = useState('');
  const [confirmingStopAll, setConfirmingStopAll] = useState(false);

  // Defense-in-depth: the tab and the API both gate this too, so a non-platform-admin never reaches a
  // working wind-down form.
  if (!isPlatformAdmin) {
    return (
      <Card data-testid="recurring-admin-forbidden">
        <CardContent className="py-6 text-sm text-muted-foreground">
          Only a platform administrator can manage recurring wind-down for a committee.
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground" data-testid="recurring-admin-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading recurring donations…
      </div>
    );
  }

  if (isError || !summary) {
    return (
      <div className="text-sm text-destructive">Could not load recurring donations for this committee.</div>
    );
  }

  const activeCount = summary.activeCount;
  const hasActive = activeCount > 0;
  const pullInError =
    pullIn.error instanceof RecurringCutoffError ? pullIn.error.message : null;

  return (
    <div className="space-y-4" data-testid="recurring-admin">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5" /> Recurring donations
          </CardTitle>
          <CardDescription>
            Monthly recurring gifts are bounded by a cutoff — the committee's latest known election. After a
            candidate loses or withdraws, pull the cutoff in or stop all recurring now so donors aren't
            charged for a finished race.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Status */}
          <dl className="grid grid-cols-2 gap-2 text-sm">
            <dt className="text-muted-foreground">Active recurring donations</dt>
            <dd className="font-medium" data-testid="recurring-active-count">{activeCount}</dd>
            <dt className="text-muted-foreground">Current cutoff</dt>
            <dd className="font-medium" data-testid="recurring-cutoff">
              {summary.cutoff ? formatCutoff(summary.cutoff) : 'None'}
            </dd>
          </dl>

          {!summary.recurringAvailable && (
            <div className="flex items-start gap-2 rounded-md bg-muted p-3 text-sm text-muted-foreground">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                Recurring donations are currently unavailable for new donors (the feature is off or there's
                no future election on file). Any existing series can still be wound down below.
              </span>
            </div>
          )}

          {/* A platform admin (the only audience that reaches here) can always wind down. */}
          <>
            {/* Pull the cutoff in */}
              <div className="space-y-2 border-t border-border pt-4">
                <Label htmlFor="recurring-cutoff-date">Pull the cutoff in</Label>
                <p className="text-sm text-muted-foreground">
                  Move every active series' cutoff to an earlier date. Series already ending on or before
                  the new date are left unchanged — the cutoff only ever moves in.
                </p>
                <div className="flex items-center gap-2">
                  <Input
                    id="recurring-cutoff-date"
                    type="date"
                    className="w-auto"
                    min={getCampaignTodayString()}
                    value={cutoff}
                    onChange={(e) => setCutoff(e.target.value)}
                    disabled={!hasActive}
                  />
                  <Button
                    onClick={() => pullIn.mutate(cutoff)}
                    disabled={!hasActive || !cutoff || pullIn.isPending}
                  >
                    {pullIn.isPending ? 'Pulling in…' : 'Pull in cutoff'}
                  </Button>
                </div>
                {pullInError && <p className="text-sm text-destructive">{pullInError}</p>}
                {pullIn.isSuccess && (
                  <p className="text-sm text-muted-foreground" data-testid="pull-in-result">
                    Updated {pullIn.data.affectedCount} series
                    {pullIn.data.failedSubscriptionIds.length > 0
                      ? ` — ${pullIn.data.failedSubscriptionIds.length} could not be updated (retry).`
                      : '.'}
                  </p>
                )}
              </div>

              {/* Stop all now */}
              <div className="space-y-2 border-t border-border pt-4">
                <Label>Stop this campaign's recurring now</Label>
                <p className="text-sm text-muted-foreground">
                  Immediately cancel every active recurring series for this committee. This can't be undone —
                  donors would need to set up a new recurring gift.
                </p>
                {!confirmingStopAll ? (
                  <Button
                    variant="destructive"
                    onClick={() => setConfirmingStopAll(true)}
                    disabled={!hasActive}
                  >
                    Stop all recurring now
                  </Button>
                ) : (
                  <div className="flex items-center gap-2" data-testid="stop-all-confirm">
                    <span className="text-sm font-medium">Cancel all {activeCount} series?</span>
                    <Button
                      variant="destructive"
                      onClick={() =>
                        stopAll.mutate(undefined, { onSettled: () => setConfirmingStopAll(false) })
                      }
                      disabled={stopAll.isPending}
                    >
                      {stopAll.isPending ? 'Stopping…' : 'Yes, stop all'}
                    </Button>
                    <Button variant="outline" onClick={() => setConfirmingStopAll(false)}>
                      Keep them
                    </Button>
                  </div>
                )}
                {stopAll.isSuccess && (
                  <p className="text-sm text-muted-foreground" data-testid="stop-all-result">
                    Canceled {stopAll.data.affectedCount} series
                    {stopAll.data.failedSubscriptionIds.length > 0
                      ? ` — ${stopAll.data.failedSubscriptionIds.length} could not be canceled (retry).`
                      : '.'}
                  </p>
                )}
              </div>
          </>
        </CardContent>
      </Card>
    </div>
  );
}

// Format an ISO `yyyy-MM-dd` cutoff for display without a timezone shift (date-only, parsed by parts).
function formatCutoff(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}
